/**
 * Safety API client — in-app reporting + user blocking, web parity with the
 * mobile app (`artifacts/mobile/lib/safety-api.ts`, App Store Guideline 1.2).
 *
 * Thin authenticated wrappers over the shared API server, following the same
 * bearer-token `authFetch` convention as `users-api.ts` / `bookings-api.ts`
 * (transient GET retry, RateLimitError on 429, one silent refresh on 401).
 * Callers own the UI (ReportDialog, chat safety menu); this module only
 * shapes the requests and exposes the friendly error type-guards.
 */

import { fetchWithTransientRetry } from "@/lib/transient-fetch";
import { RateLimitError } from "@/lib/api-error";
import { getSharedRefreshedToken, forceSessionExpiredRedirect } from "@/lib/auth-refresh";
import { ApiError } from "@/lib/users-api";

const API_BASE = `${import.meta.env.BASE_URL || "/"}api`;

function authHeaders(token: string): HeadersInit {
  return {
    "Content-Type": "application/json",
    Authorization: `Bearer ${token}`,
  };
}

async function authFetch<T>(path: string, token: string, init?: RequestInit): Promise<T> {
  const res = await fetchWithTransientRetry(`${API_BASE}${path}`, {
    ...init,
    headers: { ...authHeaders(token), ...init?.headers },
  });
  if (res.status === 204) return undefined as T;
  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    if (res.status === 429) {
      const retryAfterHeader = Number(res.headers.get("Retry-After"));
      const retryAfter =
        Number.isFinite(retryAfterHeader) && retryAfterHeader > 0 ? retryAfterHeader : 60;
      throw new RateLimitError(
        (typeof body?.message === "string" && body.message) ||
          "Too many requests. Please try again shortly.",
        retryAfter,
      );
    }
    if (res.status === 401) {
      // Token may have expired mid-flight; attempt one silent, coalesced
      // refresh (shared with users-api / bookings-api) before giving up.
      const freshToken = await getSharedRefreshedToken();
      if (freshToken && freshToken !== token) {
        const retryRes = await fetchWithTransientRetry(`${API_BASE}${path}`, {
          ...init,
          headers: { ...authHeaders(freshToken), ...init?.headers },
        });
        if (retryRes.status === 204) return undefined as T;
        if (retryRes.ok) return retryRes.json();
        const retryBody = await retryRes.json().catch(() => ({}));
        if (retryRes.status === 401 || retryBody.error === "session_expired") {
          forceSessionExpiredRedirect();
        }
        throw new ApiError(
          (typeof retryBody?.message === "string" && retryBody.message) ||
            retryBody.error ||
            `API error ${retryRes.status}`,
          retryRes.status,
          retryBody,
        );
      }
    }
    throw new ApiError(
      (typeof body?.message === "string" && body.message) ||
        body.error ||
        `API error ${res.status}`,
      res.status,
      body,
    );
  }
  return res.json();
}

// ---------------------------------------------------------------------------
// Reporting.
// ---------------------------------------------------------------------------

/** What a report points at. Mirrors the server's `target_type` enum. */
export type ReportTargetType =
  | "message"
  | "conversation"
  | "user"
  | "listing"
  | "review";

export interface CreateReportInput {
  target_type: ReportTargetType;
  target_id: string;
  /** Canonical reason id (see the dialog's REPORT_REASONS) or free text. */
  reason: string;
  /** Optional extra context the reporter typed in. */
  details?: string;
}

export interface CreateReportResult {
  id?: string | number;
  status?: string;
}

/**
 * True when a failed call was rejected by the server's per-user report rate
 * limit (HTTP 429). Lets the dialog show "You've reported a lot recently…"
 * instead of a generic error.
 */
export function isReportRateLimited(err: unknown): boolean {
  return (
    (err instanceof Error && err.name === "RateLimitError") ||
    (!!err && typeof err === "object" && (err as { status?: number }).status === 429)
  );
}

/** File a report (POST /reports). Throws on failure. */
export function createReport(
  token: string,
  input: CreateReportInput,
): Promise<CreateReportResult> {
  return authFetch<CreateReportResult>("/reports", token, {
    method: "POST",
    body: JSON.stringify({
      target_type: input.target_type,
      target_id: input.target_id,
      reason: input.reason,
      ...(input.details?.trim() ? { details: input.details.trim() } : null),
    }),
  });
}

// ---------------------------------------------------------------------------
// Blocking.
// ---------------------------------------------------------------------------

/**
 * One block the signed-in user has placed (GET /users/me/blocks). The server
 * returns the directed edge's target (numeric users.id of the person the
 * caller blocked); compare with `String(a) === String(b)` since the web app
 * carries user ids as strings.
 */
export interface BlockedUser {
  blocked_id: number;
  created_at?: string | null;
}

/** List the users the caller has blocked. Fail-soft to [] on malformed body. */
export async function fetchBlocks(token: string): Promise<BlockedUser[]> {
  const res = await authFetch<{ blocks?: BlockedUser[] }>("/users/me/blocks", token);
  return Array.isArray(res?.blocks) ? res.blocks : [];
}

/** True when `userId` is among the caller's outgoing blocks. */
export function isUserBlocked(blocks: BlockedUser[], userId: string): boolean {
  return blocks.some((b) => String(b.blocked_id) === String(userId));
}

export function blockUser(token: string, userId: string): Promise<{ blocked_id: number }> {
  return authFetch<{ blocked_id: number }>("/users/me/blocks", token, {
    method: "POST",
    body: JSON.stringify({ user_id: userId }),
  });
}

export function unblockUser(token: string, userId: string): Promise<void> {
  return authFetch<void>(`/users/me/blocks/${encodeURIComponent(userId)}`, token, {
    method: "DELETE",
  });
}

/**
 * True when a failed send / find-or-create / attachment call was the server's
 * block-enforcement 403 (`{ error: "blocked", code: "blocked" }`) — either
 * party has blocked the other, so retrying can never succeed.
 */
export function isBlockedError(err: unknown): boolean {
  if (!err || typeof err !== "object") return false;
  const e = err as { status?: number; code?: string; details?: { code?: unknown } };
  return e.status === 403 && (e.code === "blocked" || e.details?.code === "blocked");
}
